import axios from 'axios'
import { ref } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { emitOpenLoginDialog } from '@/plugins/emitter'
import { buildMyprofileImageBlob } from '@/utils/format-edit/myprofileImage'

export function useMyprofileOutput() {
  const { isAuthenticated, user } = useAuth()
  const { deviceLib, site, varuna } = useHostServices()
  const savingImage = ref(false)
  const sharingImage = ref(false)
  const imageError = ref('')
  const canShareImage =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  function logMyprofileOutput(formatId: string) {
    try {
      const uid = user.value?.uid
      if (!uid) return
      const formData = new FormData()
      formData.append('uuid', uid)
      formData.append('format_id', formatId)
      formData.append('device_flg', deviceLib.isMobileDevice ? 'SP' : 'PC')
      formData.append('app_flag', site.appFlag)
      axios
        .post(varuna.endPoints.myprofileOutputLog, formData, {
          timeout: 10000
        })
        .catch(() => {})
    } catch {
      // 記録失敗はユーザー操作に影響させない
    }
  }

  async function saveMyprofileImage() {
    if (!isAuthenticated.value) {
      emitOpenLoginDialog()
      return
    }
    imageError.value = ''
    savingImage.value = true
    try {
      const blob = await buildMyprofileImageBlob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'myprofile.png'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      logMyprofileOutput('myprofile')
    } catch (e) {
      const err = e as { message?: string }
      imageError.value = err.message || '画像の保存に失敗しました'
    } finally {
      savingImage.value = false
    }
  }

  async function shareMyprofileImage() {
    if (!isAuthenticated.value) {
      emitOpenLoginDialog()
      return
    }
    imageError.value = ''
    sharingImage.value = true
    try {
      const blob = await buildMyprofileImageBlob()
      const file = new File([blob], 'myprofile.png', { type: 'image/png' })
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'マイプロフィール' })
        logMyprofileOutput('myprofile_share')
      } else {
        throw new Error(
          'この端末は画像のシェアに対応していません。画像を保存してからシェアしてください。'
        )
      }
    } catch (e) {
      const err = e as { name?: string; message?: string }
      if (err.name !== 'AbortError') {
        imageError.value = err.message || 'シェアに失敗しました'
      }
    } finally {
      sharingImage.value = false
    }
  }

  return {
    savingImage,
    sharingImage,
    imageError,
    canShareImage,
    saveMyprofileImage,
    shareMyprofileImage
  }
}
